import type { Editor } from '@tiptap/core';
import { format, isToday, isYesterday } from 'date-fns';
import type { FormEvent } from 'react';

import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { supabase } from '@/lib/supabase';

// Helper to convert Supabase row to UserComment format
const toUserComment = (row: any): UserComment => ({
	uid: row.id,
	id: row.id,
	text: row.text,
	userName: row.user_name,
	userId: row.user_id,
	documentCreatedAt: {
		seconds: new Date(row.created_at).getTime() / 1000,
		toDate: () => new Date(row.created_at),
	},
	editorType: row.editor_type,
	commentType: row.comment_type,
	isEdited: row.is_edited,
	thread: [],
});

// Group flat comments into parent comments with thread arrays
export const groupCommentsWithReplies = (flatComments: any[]): UserComment[] => {
	const commentMap = new Map<string, UserComment>();
	const parentComments: UserComment[] = [];

	// First pass: create all comment objects
	flatComments.forEach((row) => {
		const comment = toUserComment(row);
		commentMap.set(row.id, comment);
	});

	// Second pass: group replies under parents
	flatComments.forEach((row) => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const comment = commentMap.get(row.id)!;
		if (row.parent_comment_id) {
			const parent = commentMap.get(row.parent_comment_id);
			if (parent) {
				parent.thread = parent.thread || [];
				parent.thread.push(comment);
			}
		} else {
			parentComments.push(comment);
		}
	});

	return parentComments;
};

export const sortComments = (comments: UserComment[]) => {
	return [...comments]
		.sort((a, b) => {
			const aTime = a.documentCreatedAt?.seconds || 0;
			const bTime = b.documentCreatedAt?.seconds || 0;
			if (aTime < bTime) {
				return 1;
			}
			if (aTime > bTime) {
				return -1;
			}
			return 0;
		})
		.reverse();
};

export const renderDate = (dateTime: Date) => {
	const time = 'h:mm a';
	const dateTimeFormat = 'LLL d, yyyy h:mm a';
	const formatTime = format(dateTime, time);
	const formatDate = format(dateTime, dateTimeFormat);

	if (isToday(dateTime)) {
		return `Today ${formatTime}`;
	}
	if (isYesterday(dateTime)) {
		return `Yesterday ${formatTime}`;
	}
	return formatDate;
};

export const replyToComment = async (
	e: FormEvent<HTMLButtonElement>,
	comment: UserComment,
	userName: string,
	courseId: string,
	userId: string
) => {
	const { form } = e.target as HTMLFormElement;
	const message = form.elements.comment.value.trim();

	if (!message) return;

	const schoolId = import.meta.env.VITE_SCHOOL_ID;

	try {
		const { error } = await supabase.from('course_comments').insert({
			course_id: courseId,
			parent_comment_id: comment.id, // This is a reply to the parent comment
			text: message,
			user_name: userName,
			user_id: userId,
			editor_type: comment.editorType,
			comment_type: comment.commentType || 'general',
			school_id: schoolId,
		});

		if (error) throw error;
		form.reset();
	} catch (error) {
		showErrorToast('Could not add comment. Please try again.');
	}
};

export const updateComment = async (e: FormEvent<HTMLButtonElement>, reply: UserComment) => {
	const { form } = e.target as HTMLFormElement;
	const message = form.elements.comment.value.trim();

	if (!message) return;

	try {
		// Update the comment (works for both parent and reply since they're all rows)
		const { error } = await supabase
			.from('course_comments')
			.update({
				text: message,
				is_edited: true,
			})
			.eq('id', reply.id);

		if (error) throw error;
		form.reset();
	} catch (error) {
		showErrorToast('Could not update comment. Please try again.');
	}
};

const removeCommentInEditor = (html: string, commentId: string) => {
	const pattern = `<\\s*span[^>]+data-uid="${commentId}"*>(.*?)<\\s*/\\s*span>`;
	const regex = new RegExp(pattern);
	const matchingTag = html.match(regex);

	if (matchingTag) {
		// 0 is the string used to match the regex
		// 1 is the string within the span tag
		const htmlWithoutComment = html.replace(matchingTag[0], matchingTag[1]);
		return htmlWithoutComment;
	}
	return null;
};

export const removeComment = async (
	comment: UserComment,
	reply: UserComment,
	_courseId: string,
	editorList: Record<string, Editor>,
	onSuccess?: () => void
) => {
	const editor = editorList[comment.editorType];
	if (editor) {
		const html = editor.getHTML();
		const updatedHTML = removeCommentInEditor(html, comment.id);

		// Updates the editor only if it is the parent comment being deleted
		if (updatedHTML && reply.id === comment.id) {
			editor.commands.setContent(updatedHTML);
		}
	}

	try {
		// Delete the comment (cascade will handle replies if it's a parent)
		const { error } = await supabase.from('course_comments').delete().eq('id', reply.id);
		if (error) throw error;
		showSuccessToast('Comment Deleted');
		onSuccess?.();
	} catch (error) {
		showErrorToast('Comment could not be deleted');
	}
};
